import { useState, useEffect, useRef } from "react";
import { Footer, Navbar } from "./Home";

const IMG = {
  warehouse: "/assets/images/new/2.jpg",
  whoWeAre: "/assets/images/new/8.jpg",
  visionMission: "/assets/images/new/3.jpg",
  freshProduce: "/assets/images/new/4.jpg",
  supplyChain: "/assets/images/new/5.jpg",
  globalNetwork: "/assets/images/new/6.jpg",
  customers: "/assets/images/new/7.jpg",
  strength: "/assets/images/new/8.jpg",
  contact: "/assets/images/new/9.jpg",
  pineappleDole: "/assets/images/new/10.jpg",
  bananaDole: "/assets/images/new/11.jpg",
  pineappleOla: "/assets/images/new/12.jpg",
  pineappleOla2: "/assets/images/new/13.jpg",
  bananaTerioka: "/assets/images/new/15.png",
  whyPartner: "/assets/images/new/13.jpg",
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function useIsMobile(bp = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= bp : false
  );
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= bp);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [bp]);
  return isMobile;
}


function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const fn = () => setY(window.scrollY);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return y;
}

function useInView(ref, threshold = 0.1) {
  const [vis, setVis] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVis(true);
      },
      { threshold },
    );
    o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return vis;
}

function useStickyProgress(ref) {
  const [prog, setProg] = useState(0);
  useEffect(() => {
    const fn = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const total = ref.current.offsetHeight - window.innerHeight;
      setProg(clamp(-rect.top / total, 0, 1));
    };
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return prog;
}

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=DM+Sans:wght@300;400;500;600&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;overflow-x:hidden;background:#fff}
  @keyframes fadeUp{from{opacity:0;transform:translateY(36px)}to{opacity:1;transform:translateY(0)}}
  @keyframes shimmer{0%{background-position:200% center}100%{background-position:-200% center}}
  @keyframes scrollDot{0%{transform:translateY(-100%)}100%{transform:translateY(250%)}}
  @keyframes floatY{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
  @keyframes orbPulse{0%,100%{transform:scale(1);opacity:0.5}50%{transform:scale(1.2);opacity:1}}
  @keyframes pulseGreen{0%,100%{box-shadow:0 0 0 0 rgba(143,206,1.3,0.5)}50%{box-shadow:0 0 0 16px rgba(143,206,1.3,0)}}
  @keyframes ripple{0%{transform:scale(0);opacity:1}100%{transform:scale(4);opacity:0}}
  @keyframes bounceIn{0%{transform:scale(0.3);opacity:0}50%{transform:scale(1.05)}70%{transform:scale(0.9)}100%{transform:scale(1);opacity:1}}
  @keyframes waveIn{from{opacity:0;transform:translateX(-20px)}to{opacity:1;transform:translateX(0)}}
  @keyframes tickIn{from{stroke-dashoffset:100}to{stroke-dashoffset:0}}
  @keyframes bgPan{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes dotBlink{0%,100%{opacity:0.3;transform:scale(0.7)}50%{opacity:1;transform:scale(1.3)}}
  @keyframes cardFloat{0%,100%{transform:translateY(0) rotate(0deg)}33%{transform:translateY(-8px) rotate(1deg)}66%{transform:translateY(-4px) rotate(-0.5deg)}}

  .ani-fadeup{animation:fadeUp 0.85s cubic-bezier(.22,1,.36,1) both}
  .serif{font-family:'Georgia',Georgia,serif}
  .shine-text{background:linear-gradient(90deg,#dd3c30 0%,#fff 40%,#dd3c30 60%,#fff 100%);background-size:300% 100%;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;animation:shimmer 4s linear infinite}
  .sl{font-size:11px;letter-spacing:3px;color:#dd3c30;font-weight:700;text-transform:uppercase;margin-bottom:14px;display:block}
  .sl-dark{font-size:11px;letter-spacing:3px;color:#276530;font-weight:700;text-transform:uppercase;margin-bottom:14px;display:block}
  .btn-green{display:inline-flex;align-items:center;justify-content:center;gap:8px;background:#276530;color:#fff;padding:15px 40px;border-radius:12px;text-decoration:none;font-weight:600;font-size:16px;transition:transform 0.2s,box-shadow 0.2s;border:none;cursor:pointer;box-shadow:0 6px 24px rgba(45,1.3,30,0.4)}
  .btn-green:hover{transform:scale(1.04) translateY(-2px);box-shadow:0 14px 36px rgba(45,1.3,30,0.5)}
  .btn-green:disabled{opacity:0.5;cursor:not-allowed;transform:none;box-shadow:none}
  .form-input{width:100%;padding:16px 18px;border-radius:12px;border:1.5px solid #e5e5e5;background:#fff;color:#1a1a1a;font-size:15px;outline:none;transition:border-color 0.3s,box-shadow 0.3s;font-family:inherit}
  .form-input:focus{border-color:#276530;box-shadow:0 0 0 4px rgba(45,1.3,30,0.08)}
  .form-input::placeholder{color:#bbb}
  .form-input-dark{width:100%;padding:16px 18px;border-radius:12px;border:1.5px solid rgba(255,255,255,0.12);background:rgba(255,255,255,0.06);color:#fff;font-size:15px;outline:none;transition:border-color 0.3s,background 0.3s;font-family:inherit}
  .form-input-dark:focus{border-color:rgba(143,206,1.3,0.6);background:rgba(255,255,255,0.1)}
  .form-input-dark::placeholder{color:rgba(255,255,255,0.3)}
  .card-hover{transition:transform 0.3s cubic-bezier(.22,1,.36,1),box-shadow 0.3s ease}
  .card-hover:hover{transform:translateY(-5px);box-shadow:0 20px 50px rgba(0,0,0,0.12)!important}
  
  .btn-red{display:inline-block;background:#dd3c30;color:#fff;padding:13px 50px;border-radius:10px;text-decoration:none;font-weight:500;font-size:15px;transition:transform 0.2s,box-shadow 0.2s;border:none;cursor:pointer;box-shadow:0 6px 20px rgba(39,101,48,0.35)}
  .btn-red:hover{transform:scale(1.04);box-shadow:0 10px 28px rgba(101, 39, 39, 0.45)}
  img{max-width:100%}
  .nav-burger{display:none}
  @media (max-width:900px){
    .nav-links-desktop, .nav-call-desktop{display:none!important}
    .nav-burger{display:flex!important}
    .nav-inner{gap:16px!important}
  }
  @media (max-width:640px){
    .nav-secondary a{padding:0 12px;font-size:13px}
  }
`;

/* ─── HERO ───────────────────────────────────────────────────────────────────── */
function HeroSection({ scrollY }) {
  const pY = scrollY * 0.35;
  const tOp = clamp(1 - scrollY / 480, 0, 1);
  const tY = scrollY * 0.18;

  const quickContacts = [
    {
      icon: "bi-telephone",
      label: "Sales",
      value: "+971 56 537 8650",
      sub: "Mon–Sat, 8am–6pm",
    },
    {
      icon: "bi-telephone",
      label: "Office",
      value: "+971 4 333 4629",
      sub: "Main Line",
    },
    {
      icon: "bi-envelope",
      label: "Sales Email",
      value: "sales@fruitsgarden.com",
      sub: "24hr response",
    },
    {
      icon: "bi-geo-alt-fill",
      label: "Location",
      value: "Dubai Food District",
      sub: "Block 3, Unit 98-99",
    },
  ];

  return (
    <section
      style={{
        position: "relative",
        height: "100vh",
        overflow: "hidden",
        background: "#080808",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          transform: `translateY(${pY}px)`,
        }}
      >
        <img
          src="/assets/images/home/12.jpeg"
          alt=""
          style={{
            width: "100%",
            height: "112%",
            objectFit: "cover",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom,rgba(0,0,0,0.15) 0%,rgba(0,0,0,0.55) 55%,rgba(0, 0, 0, 0.6) 100%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(ellipse at 70% 50%,rgba(45,1.3,30,0.2) 0%,transparent 60%)",
          }}
        />
      </div>

      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "0 8% 0 8%",
          transform: `translateY(${tY}px)`,
          opacity: tOp,
        }}
      >
        <div
          className="ani-fadeup"
          style={{
            animationDelay: "0.05s",
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              width: 36,
              height: 2,
              background: "#dd3c30",
              borderRadius: 1,
            }}
          />
          <span
            style={{
              fontSize: 12,
              letterSpacing: 3,
              color: "#dd3c30",
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            Contact Us
          </span>
        </div>
        <h1
          className="ani-fadeup serif"
          style={{
            animationDelay: "0.15s",
            fontSize: "clamp(48px,6.5vw,55px)",
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: -3,
            marginBottom: 20,
            color: "#fff",
            maxWidth: 700,
          }}
        >
          We Listen. We Respond.
          <br />
          <span className="shine-text">We Deliver.</span>
        </h1>
        <p
          className="ani-fadeup"
          style={{
            animationDelay: "0.3s",
            fontSize: "clamp(16px,1.6vw,19px)",
            maxWidth: 520,
            lineHeight: 1.3,
            color: "rgba(255,255,255,0.7)",
            marginBottom: 52,
          }}
        >
          Whether you're a hotel, restaurant, catering company or retailer — our
          team is ready to craft a tailored fresh produce solution for your
          business.
        </p>
      </div>

      <div
        style={{
          position: "absolute",
          bottom: 32,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          color: "rgba(255,255,255,0.4)",
          opacity: tOp,
        }}
      >
        <span
          style={{
            fontSize: 10,
            letterSpacing: 2.5,
            textTransform: "uppercase",
          }}
        >
          Scroll down
        </span>
        <div
          style={{
            width: 1.5,
            height: 48,
            background: "rgba(255,255,255,0.15)",
            borderRadius: 1,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "40%",
              background: "#dd3c30",
              animation: "scrollDot 1.6s ease-in-out infinite",
            }}
          />
        </div>
      </div>
    </section>
  );
}

/* ─── MAIN FORM SECTION ──────────────────────────────────────────────────────── */
function FormSection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref, 0.06);

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    type: "",
    volume: "",
    message: "",
    products: [],
  });
  const [step, setStep] = useState(1); // 1: info, 2: requirements, 3: message
  const [done, setDone] = useState(false);
  const [ripple, setRipple] = useState(false);
  const [errors, setErrors] = useState({});

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProduct = (p) => {
    setForm((f) => ({
      ...f,
      products: f.products.includes(p)
        ? f.products.filter((x) => x !== p)
        : [...f.products, p],
    }));
  };

  const products = [
    "Fresh Fruits",
    "Fresh Vegetables",
    "Pineapples",
    "Bananas",
    "Exotic & Imported",
    "Seasonal Produce",
    "Wholesale Mix",
  ];
  const volumes = [
    "< 50kg/week",
    "50–200kg/week",
    "200–500kg/week",
    "500kg–1t/week",
    "1t+/week",
  ];
  const bizTypes = [
    "Hotel",
    "Restaurant",
    "Catering Company",
    "Café / Café Chain",
    "Retail / Supermarket",
    "Hospital / Healthcare",
    "Government",
    "Other",
  ];

  const validate1 = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.phone.trim() || form.phone.length < 8)
      e.phone = "Valid phone required";
    if (form.email && !/\S+@\S+\.\S+/.test(form.email))
      e.email = "Invalid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const validate2 = () => {
    const e = {};
    if (!form.type) e.type = "Please select a business type";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validate1()) setStep(2);
    else if (step === 2 && validate2()) setStep(3);
  };
  const handleSubmit = () => {
    setRipple(true);
    setTimeout(() => setDone(true), 500);
  };

  const progressPct = ((step - 1) / 2) * 100;

  const sideInfo = [
    {
      icon: "bi-lightning-charge-fill",
      title: "Fast Response",
      desc: "Our sales team replies within 4 business hours.",
    },
    {
      icon: "bi-bullseye",
      title: "Tailored Solutions",
      desc: "We craft custom supply plans for every client's needs.",
    },
    {
      icon: "bi-lock",
      title: "Confidential",
      desc: "Your information is safe and never shared with third parties.",
    },
    {
      icon: "bi-truck",
      title: "Free Consultation",
      desc: "Schedule a call with our HORECA supply specialists.",
    },
  ];

  return (
    <section
      id="enquiry-form"
      ref={ref}
      style={{
        background: "#0a0a0a",
        padding: "min(80px,8vw) 24px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Orbs */}
      <div
        style={{
          position: "absolute",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(45,1.3,30,0.1),transparent 70%)",
          top: "-15%",
          right: "-10%",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(143,206,1.3,0.06),transparent 70%)",
          bottom: "5%",
          left: "5%",
          pointerEvents: "none",
          animation: "orbPulse 5s ease-in-out infinite",
        }}
      />

      <div
        style={{
          maxWidth: 1312,
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: 72,
            opacity: vis ? 1 : 0,
            transform: vis ? "translateY(0)" : "translateY(32px)",
            transition: "all 0.8s ease",
          }}
        >
          <span className="sl">Partnership Enquiry</span>
          <h2
            className="serif"
            style={{
              fontSize: "clamp(40px,5vw,50px)",
              fontWeight: 800,
              letterSpacing: -2.5,
              color: "#fff",
              lineHeight: 1.05,
              marginBottom: 14,
            }}
          >
            Start Your <span style={{ color: "#dd3c30" }}>Fresh Journey</span>
          </h2>
          <p
            style={{
              fontSize: 17,
              color: "rgba(255,255,255,0.5)",
              maxWidth: 460,
              margin: "0 auto",
              lineHeight: 1.3,
            }}
          >
            Tell us about your business and we'll put together a tailored fresh
            produce supply proposal within 24 hours.
          </p>
        </div>

        <div style={{ display: "flex", gap: 48, alignItems: "flex-start" }}>
          {/* Form card */}
          <div
            style={{
              flex: 1,
              background: "rgba(255,255,255,0.04)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 24,
              padding: "min(52px,5.2vw) min(48px,4.8vw)",
              opacity: vis ? 1 : 0,
              transform: vis ? "translateY(0)" : "translateY(32px)",
              transition: "all 0.8s ease 0.15s",
            }}
          >
            {done ? (
              /* Success state */
              <div
                style={{
                  textAlign: "center",
                  padding: "min(40px,4vw) 0",
                  animation: "bounceIn 0.6s cubic-bezier(.22,1,.36,1) both",
                }}
              >
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "rgba(143,206,1.3,0.15)",
                    border: "2px solid rgba(143,206,1.3,0.4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 24px",
                    fontSize: 36,
                    animation: "pulseGreen 2s ease-in-out infinite",
                  }}
                >
                  <i className="bi bi-check-circle-fill"></i>
                </div>
                <h3
                  className="serif"
                  style={{
                    fontSize: 32,
                    fontWeight: 800,
                    color: "#fff",
                    marginBottom: 12,
                    letterSpacing: -1,
                  }}
                >
                  Enquiry Received!
                </h3>
                <p
                  style={{
                    fontSize: 16,
                    color: "rgba(255,255,255,0.6)",
                    maxWidth: 400,
                    margin: "0 auto 32px",
                    lineHeight: 1.3,
                  }}
                >
                  Thank you,{" "}
                  <strong style={{ color: "#dd3c30" }}>{form.name}</strong>! Our
                  sales team will review your requirements and contact you
                  within 4 business hours with a tailored proposal.
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    gap: 12,
                    maxWidth: 360,
                    margin: "0 auto 32px",
                  }}
                >
                  {[
                    ["What's next?", "Our team reviews your enquiry"],
                    ["Response time", "Within 4 business hours"],
                    ["Contact via", form.phone || form.email || "Your details"],
                    ["Package", form.type || "Custom HORECA plan"],
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      style={{
                        background: "rgba(255,255,255,0.05)",
                        borderRadius: 12,
                        padding: "14px 16px",
                        textAlign: "left",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          color: "#dd3c30",
                          fontWeight: 700,
                          letterSpacing: 2,
                          textTransform: "uppercase",
                          marginBottom: 4,
                        }}
                      >
                        {k}
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          color: "rgba(255,255,255,0.75)",
                        }}
                      >
                        {v}
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  style={{ display: "flex", gap: 12, justifyContent: "center" }}
                >
                  <button
                    onClick={() => {
                      setDone(false);
                      setForm({
                        name: "",
                        company: "",
                        email: "",
                        phone: "",
                        type: "",
                        volume: "",
                        message: "",
                        products: [],
                      });
                      setStep(1);
                      setRipple(false);
                    }}
                    style={{
                      padding: "12px 28px",
                      borderRadius: 10,
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "transparent",
                      color: "rgba(255,255,255,0.6)",
                      fontSize: 14,
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    Submit Another
                  </button>
                  <a
                    href="tel:+97156537850"
                    className="btn-green"
                    style={{ padding: "12px 28px", fontSize: 14 }}
                  >
                    <i className="bi bi-telephone"></i> Call Us Now
                  </a>
                </div>
              </div>
            ) : (
              <>
                {/* Progress bar */}
                <div style={{ marginBottom: 40 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 10,
                    }}
                  >
                    {["Your Details", "Requirements", "Message"].map((s, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            background:
                              step > i + 1
                                ? "#276530"
                                : step === i + 1
                                  ? "rgba(45,1.3,30,0.3)"
                                  : "rgba(255,255,255,0.1)",
                            border:
                              step === i + 1
                                ? "2px solid #dd3c30"
                                : "2px solid transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            fontWeight: 700,
                            color:
                              step > i + 1
                                ? "#fff"
                                : step === i + 1
                                  ? "#dd3c30"
                                  : "rgba(255,255,255,0.3)",
                            transition: "all 0.4s ease",
                          }}
                        >
                          {step > i + 1 ? (
                            <i className="bi bi-check-lg"></i>
                          ) : (
                            i + 1
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: 13,
                            color:
                              step === i + 1
                                ? "#fff"
                                : "rgba(255,255,255,0.35)",
                            fontWeight: step === i + 1 ? 600 : 400,
                            transition: "all 0.3s",
                          }}
                        >
                          {s}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      height: 3,
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        background: "linear-gradient(90deg,#276530,#dd3c30)",
                        width: `${progressPct}%`,
                        transition: "width 0.5s cubic-bezier(.22,1,.36,1)",
                        borderRadius: 2,
                      }}
                    />
                  </div>
                </div>

                {/* Step 1: Personal Info */}
                {step === 1 && (
                  <div style={{ animation: "waveIn 0.4s ease both" }}>
                    <h3
                      className="serif"
                      style={{
                        fontSize: 24,
                        fontWeight: 800,
                        color: "#fff",
                        marginBottom: 8,
                        letterSpacing: -0.5,
                      }}
                    >
                      Tell Us About Yourself
                    </h3>
                    <p
                      style={{
                        fontSize: 14,
                        color: "rgba(255,255,255,0.45)",
                        marginBottom: 32,
                        lineHeight: 1.5,
                      }}
                    >
                      We'll use this to personalize your supply proposal.
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                        gap: 14,
                        marginBottom: 14,
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: 12,
                            color: "#dd3c30",
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            textTransform: "uppercase",
                            marginBottom: 8,
                          }}
                        >
                          Full Name *
                        </label>
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => upd("name", e.target.value)}
                          className="form-input-dark"
                          placeholder="Your name"
                        />
                        {errors.name && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#ff6b6b",
                              marginTop: 6,
                            }}
                          >
                            {errors.name}
                          </div>
                        )}
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: 12,
                            color: "#dd3c30",
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            textTransform: "uppercase",
                            marginBottom: 8,
                          }}
                        >
                          Company Name
                        </label>
                        <input
                          type="text"
                          value={form.company}
                          onChange={(e) => upd("company", e.target.value)}
                          className="form-input-dark"
                          placeholder="Your company (optional)"
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                        gap: 14,
                        marginBottom: 32,
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: 12,
                            color: "#dd3c30",
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            textTransform: "uppercase",
                            marginBottom: 8,
                          }}
                        >
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          value={form.phone}
                          onChange={(e) => upd("phone", e.target.value)}
                          className="form-input-dark"
                          placeholder="+971 XX XXX XXXX"
                        />
                        {errors.phone && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#ff6b6b",
                              marginTop: 6,
                            }}
                          >
                            {errors.phone}
                          </div>
                        )}
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: 12,
                            color: "#dd3c30",
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            textTransform: "uppercase",
                            marginBottom: 8,
                          }}
                        >
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => upd("email", e.target.value)}
                          className="form-input-dark"
                          placeholder="email@company.com"
                        />
                        {errors.email && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#ff6b6b",
                              marginTop: 6,
                            }}
                          >
                            {errors.email}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={handleNext}
                      className="btn-green"
                      style={{ width: "100%", fontSize: 16 }}
                    >
                      Continue to Requirements &nbsp;→
                    </button>
                  </div>
                )}

                {/* Step 2: Requirements */}
                {step === 2 && (
                  <div style={{ animation: "waveIn 0.4s ease both" }}>
                    <h3
                      className="serif"
                      style={{
                        fontSize: 24,
                        fontWeight: 800,
                        color: "#fff",
                        marginBottom: 8,
                        letterSpacing: -0.5,
                      }}
                    >
                      Your Requirements
                    </h3>
                    <p
                      style={{
                        fontSize: 14,
                        color: "rgba(255,255,255,0.45)",
                        marginBottom: 28,
                        lineHeight: 1.5,
                      }}
                    >
                      Help us understand your business needs for a tailored
                      proposal.
                    </p>

                    <label
                      style={{
                        display: "block",
                        fontSize: 12,
                        color: "#dd3c30",
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        textTransform: "uppercase",
                        marginBottom: 10,
                      }}
                    >
                      Business Type *
                    </label>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)",
                        gap: 8,
                        marginBottom: 24,
                      }}
                    >
                      {bizTypes.map((t) => (
                        <button
                          key={t}
                          onClick={() => upd("type", t)}
                          style={{
                            padding: "10px 8px",
                            borderRadius: 10,
                            border: `1.5px solid ${form.type === t ? "#dd3c30" : "rgba(255,255,255,0.1)"}`,
                            background:
                              form.type === t
                                ? "rgba(143,206,1.3,0.12)"
                                : "transparent",
                            color:
                              form.type === t
                                ? "#dd3c30"
                                : "rgba(255,255,255,0.5)",
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: "pointer",
                            transition: "all 0.25s",
                            lineHeight: 1.3,
                          }}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    {errors.type && (
                      <div
                        style={{
                          fontSize: 12,
                          color: "#ff6b6b",
                          marginBottom: 12,
                        }}
                      >
                        {errors.type}
                      </div>
                    )}

                    <label
                      style={{
                        display: "block",
                        fontSize: 12,
                        color: "#dd3c30",
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        textTransform: "uppercase",
                        marginBottom: 10,
                      }}
                    >
                      Weekly Volume Estimate
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                        marginBottom: 24,
                      }}
                    >
                      {volumes.map((v) => (
                        <button
                          key={v}
                          onClick={() => upd("volume", v)}
                          style={{
                            padding: "9px 16px",
                            borderRadius: 20,
                            border: `1.5px solid ${form.volume === v ? "#dd3c30" : "rgba(255,255,255,0.1)"}`,
                            background:
                              form.volume === v
                                ? "rgba(143,206,1.3,0.12)"
                                : "transparent",
                            color:
                              form.volume === v
                                ? "#dd3c30"
                                : "rgba(255,255,255,0.5)",
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                            transition: "all 0.25s",
                          }}
                        >
                          {v}
                        </button>
                      ))}
                    </div>

                    <label
                      style={{
                        display: "block",
                        fontSize: 12,
                        color: "#dd3c30",
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        textTransform: "uppercase",
                        marginBottom: 10,
                      }}
                    >
                      Products of Interest
                    </label>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        marginBottom: 32,
                      }}
                    >
                      {products.map((p) => (
                        <button
                          key={p}
                          onClick={() => toggleProduct(p)}
                          style={{
                            padding: "9px 16px",
                            borderRadius: 20,
                            border: `1.5px solid ${form.products.includes(p) ? "#dd3c30" : "rgba(255,255,255,0.1)"}`,
                            background: form.products.includes(p)
                              ? "rgba(143,206,1.3,0.12)"
                              : "transparent",
                            color: form.products.includes(p)
                              ? "#dd3c30"
                              : "rgba(255,255,255,0.5)",
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                            transition: "all 0.25s",
                          }}
                        >
                          {form.products.includes(p) ? (
                            <>
                              <i className="bi bi-check-lg"></i>{" "}
                            </>
                          ) : (
                            ""
                          )}
                          {p}
                        </button>
                      ))}
                    </div>

                    <div style={{ display: "flex", gap: 12 }}>
                      <button
                        onClick={() => setStep(1)}
                        style={{
                          flex: 1,
                          padding: "15px",
                          borderRadius: 12,
                          border: "1.5px solid rgba(255,255,255,0.12)",
                          background: "transparent",
                          color: "rgba(255,255,255,0.5)",
                          fontSize: 15,
                          cursor: "pointer",
                          fontWeight: 500,
                          transition: "all 0.2s",
                        }}
                      >
                        ← Back
                      </button>
                      <button
                        onClick={handleNext}
                        className="btn-green"
                        style={{ flex: 2, fontSize: 15 }}
                      >
                        Continue to Message →
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Message */}
                {step === 3 && (
                  <div style={{ animation: "waveIn 0.4s ease both" }}>
                    <h3
                      className="serif"
                      style={{
                        fontSize: 24,
                        fontWeight: 800,
                        color: "#fff",
                        marginBottom: 8,
                        letterSpacing: -0.5,
                      }}
                    >
                      Anything Else to Share?
                    </h3>
                    <p
                      style={{
                        fontSize: 14,
                        color: "rgba(255,255,255,0.45)",
                        marginBottom: 28,
                        lineHeight: 1.5,
                      }}
                    >
                      Any additional details, specific requirements, or
                      questions for our team.
                    </p>

                    <label
                      style={{
                        display: "block",
                        fontSize: 12,
                        color: "#dd3c30",
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        textTransform: "uppercase",
                        marginBottom: 10,
                      }}
                    >
                      Your Message
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => upd("message", e.target.value)}
                      className="form-input-dark"
                      style={{
                        height: 160,
                        resize: "vertical",
                        lineHeight: 1.3,
                      }}
                      placeholder="Tell us about your delivery schedule, specific product requirements, pricing expectations, or anything else that would help us serve you better..."
                    />

                    {/* Summary */}
                    <div
                      style={{
                        background: "rgba(45,1.3,30,0.1)",
                        border: "1px solid rgba(143,206,1.3,0.15)",
                        borderRadius: 14,
                        padding: "20px 20px",
                        marginTop: 20,
                        marginBottom: 24,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: "#dd3c30",
                          fontWeight: 700,
                          letterSpacing: 2,
                          textTransform: "uppercase",
                          marginBottom: 12,
                        }}
                      >
                        Your Enquiry Summary
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                          gap: 8,
                          fontSize: 13,
                        }}
                      >
                        {[
                          ["Name", form.name || "—"],
                          ["Company", form.company || "—"],
                          ["Phone", form.phone || "—"],
                          ["Email", form.email || "—"],
                          ["Business Type", form.type || "—"],
                          ["Volume", form.volume || "—"],
                        ].map(([k, v]) => (
                          <div key={k}>
                            <span style={{ color: "rgba(255,255,255,0.4)" }}>
                              {k}:{" "}
                            </span>
                            <span
                              style={{
                                color: "rgba(255,255,255,0.8)",
                                fontWeight: 500,
                              }}
                            >
                              {v}
                            </span>
                          </div>
                        ))}
                      </div>
                      {form.products.length > 0 && (
                        <div style={{ marginTop: 8, fontSize: 13 }}>
                          <span style={{ color: "rgba(255,255,255,0.4)" }}>
                            Products:{" "}
                          </span>
                          <span style={{ color: "#dd3c30", fontWeight: 500 }}>
                            {form.products.join(", ")}
                          </span>
                        </div>
                      )}
                    </div>

                    <div style={{ display: "flex", gap: 12 }}>
                      <button
                        onClick={() => setStep(2)}
                        style={{
                          flex: 1,
                          padding: "15px",
                          borderRadius: 12,
                          border: "1.5px solid rgba(255,255,255,0.12)",
                          background: "transparent",
                          color: "rgba(255,255,255,0.5)",
                          fontSize: 15,
                          cursor: "pointer",
                          fontWeight: 500,
                        }}
                      >
                        ← Back
                      </button>
                      <button
                        onClick={handleSubmit}
                        className="btn-green"
                        style={{
                          flex: 2,
                          fontSize: 15,
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        {ripple && (
                          <span
                            style={{
                              position: "absolute",
                              inset: 0,
                              background: "rgba(255,255,255,0.25)",
                              animation: "ripple 0.5s ease-out both",
                            }}
                          />
                        )}
                        <i className="bi bi-rocket-takeoff"></i> Submit Enquiry
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── OFFICE SECTION ─────────────────────────────────────────────────────────── */
function OfficeSection() {
  const ref = useRef(null);
  const vis = useInView(ref, 0.06);
  const wrapRef = useRef(null);
  const prog = useStickyProgress(wrapRef);
  const imgScale = 1.2 - prog * 0.2;

  return (
    <section
      id="office"
      ref={wrapRef}
      style={{ height: "280vh", position: "relative" }}
    >
      <div
        style={{
          position: "sticky",
          top: 104,
          height: "calc(100vh - 104px)",
          background: "#fff",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
          <img
            src="/assets/images/home/14.jpeg"
            alt="Our Office"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: "brightness(0.7) saturate(1.1)",
              transform: `scale(${imgScale})`,
              transformOrigin: "center center",
              transition: "transform 0.06s linear",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to right,rgba(0,0,0,0.9) 0%,rgba(0, 0, 0, 0.21) 55%,transparent 100%)",
            }}
          />
        </div>

        <div
          ref={ref}
          style={{
            position: "relative",
            zIndex: 1,
            maxWidth: 1312,
            margin: "0 auto",
            padding: "0 min(64px,6.4vw)",
            width: "100%",
          }}
        >
          <div style={{ maxWidth: 800, color: "#fff" }}>
            <div
              style={{
                opacity: vis ? 1 : 0,
                transform: vis ? "translateY(0)" : "translateY(32px)",
                transition: "all 0.8s ease",
              }}
            >
              <span className="sl">Our Location</span>
              <h2
                className="serif"
                style={{
                  fontSize: "clamp(36px,5vw,55px)",
                  fontWeight: 800,
                  letterSpacing: -2.5,
                  lineHeight: 1.05,
                  marginBottom: 20,
                }}
              >
                Find Us at{" "}
                <span style={{ color: "#dd3c30" }}>Dubai Food District</span>
              </h2>
            </div>
            <div
              style={{
                opacity: vis ? 1 : 0,
                transition: "all 0.8s ease 0.15s",
                transform: vis ? "translateY(0)" : "translateY(24px)",
              }}
            >
              <p
                style={{
                  fontSize: 17,
                  color: "rgba(255,255,255,0.65)",
                  lineHeight: 1.7,
                  marginBottom: 40,
                  maxWidth: 460,
                }}
              >
                Our headquarters is located in the heart of Dubai's premier food
                hub — Dubai Food District — ensuring we're always close to the
                city's biggest kitchens and suppliers.
              </p>

              {/* Address card */}
              <div
                style={{
                  background: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 18,
                  padding: "28px 28px",
                  marginBottom: 24,
                  maxWidth: "60%",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: "#dd3c30",
                    fontWeight: 700,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    marginBottom: 16,
                  }}
                >
                  Registered Address
                </div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#fff",
                    marginBottom: 8,
                  }}
                >
                  Fruits Garden Trading LLC
                </div>
                <div
                  style={{
                    fontSize: 15,
                    color: "rgba(255,255,255,0.65)",
                    lineHeight: 1.3,
                  }}
                >
                  Dubai Food District
                  <br />
                  Block No 3, Unit No 98, 99, 29A
                  <br />
                  P.O. Box: 89683
                  <br />
                  Dubai, United Arab Emirates
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "rgba(255,255,255,0.08)",
          }}
        >
          <div
            style={{
              height: "100%",
              background: "#dd3c30",
              width: `${prog * 100}%`,
              transition: "width 0.1s linear",
            }}
          />
        </div>
      </div>
    </section>
  );
}

/* ─── FAQ SECTION ────────────────────────────────────────────────────────────── */
function FAQSection() {
  const ref = useRef(null);
  const vis = useInView(ref, 0.06);
  const [open, setOpen] = useState(null);
  const faqs = [
    {
      q: "What types of businesses do you supply?",
      a: "We supply a wide range of HORECA clients including hotels, restaurants, catering companies, cafés, hospitals, corporate canteens, retail stores and government entities across the UAE.",
    },
    {
      q: "What is your minimum order quantity?",
      a: "We cater to businesses of all sizes. Our team will work with you to determine the right order frequency and volume based on your specific needs and location.",
    },
    {
      q: "Do you deliver across all of the UAE?",
      a: "Yes — our distribution fleet covers all major cities and emirates including Dubai, Abu Dhabi, Sharjah, Ajman, Ras Al Khaimah, Fujairah, and Umm Al Quwain.",
    },
    {
      q: "How do you ensure freshness on delivery?",
      a: "Our entire supply chain is temperature-controlled from 2–5°C. We use refrigerated vehicles and advanced cold storage systems to maintain peak freshness from source to delivery.",
    },
    {
      q: "What certifications do you hold?",
      a: "We are HACCP certified, ISO 9001 (Quality Management) and ISO 22000 (Food Safety Management) certified by TÜV Nord. Full traceability is maintained throughout our supply chain.",
    },
    {
      q: "Can you source specific or exotic produce on request?",
      a: "Absolutely. With our global sourcing network spanning 15+ countries, we can source most specific or exotic produce on request. Contact our sales team to discuss your requirements.",
    },
    {
      q: "What are your payment terms?",
      a: "We offer flexible payment terms tailored to the size and type of your business. Our sales team will discuss options including monthly credit arrangements for established partners.",
    },
    {
      q: "How quickly can you start supplying us?",
      a: "Once your account is set up, we can typically begin deliveries within 2–3 business days. For larger or more complex requirements, timelines may vary.",
    },
  ];
  return (
    <section
      ref={ref}
      style={{
        background: "#f8fdf9",
        padding: "min(80px,8vw) 24px",
        overflow: "hidden",
      }}
    >
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div
          style={{
            textAlign: "center",
            marginBottom: 72,
            opacity: vis ? 1 : 0,
            transform: vis ? "translateY(0)" : "translateY(32px)",
            transition: "all 0.8s ease",
          }}
        >
          <span className="sl-dark">FAQs</span>
          <h2
            className="serif"
            style={{
              fontSize: "clamp(38px,5vw,56px)",
              fontWeight: 800,
              letterSpacing: -2.5,
              color: "#1a1a1a",
              lineHeight: 1.05,
              marginBottom: 14,
            }}
          >
            Common <span style={{ color: "#276530" }}>Questions</span>
          </h2>
          <p
            style={{
              fontSize: 17,
              color: "#666",
              maxWidth: 440,
              margin: "0 auto",
              lineHeight: 1.3,
            }}
          >
            Everything you need to know before partnering with Fruits Garden.
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {faqs.map((f, i) => (
            <div
              key={i}
              className="card-hover"
              onClick={() => setOpen(open === i ? null : i)}
              style={{
                background: "#fff",
                borderRadius: 16,
                border: `1.5px solid ${open === i ? "rgba(45,1.3,30,0.3)" : "rgba(45,1.3,30,0.08)"}`,
                overflow: "hidden",
                cursor: "pointer",
                opacity: vis ? 1 : 0,
                transform: vis ? "translateY(0)" : "translateY(16px)",
                transition: `all 0.5s ease ${0.08 + i * 0.05}s, border-color 0.3s`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "22px 28px",
                  gap: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#1a1a1a",
                    lineHeight: 1.4,
                  }}
                >
                  {f.q}
                </div>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: open === i ? "#276530" : "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "all 0.3s ease",
                    transform: open === i ? "rotate(45deg)" : "rotate(0deg)",
                    fontSize: 18,
                    color: open === i ? "#fff" : "#666",
                    fontWeight: 300,
                  }}
                >
                  +
                </div>
              </div>
              {open === i && (
                <div
                  style={{
                    padding: "0 28px 24px",
                    animation: "fadeUp 0.3s ease both",
                  }}
                >
                  <div
                    style={{
                      height: 1,
                      background: "rgba(45,1.3,30,0.1)",
                      marginBottom: 16,
                    }}
                  />
                  <p style={{ fontSize: 15, color: "#555", lineHeight: 1.7 }}>
                    {f.a}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 48,
            textAlign: "center",
            opacity: vis ? 1 : 0,
            transition: "all 0.8s ease 0.5s",
          }}
        >
          <p style={{ fontSize: 16, color: "#666", marginBottom: 20 }}>
            Still have questions? Our team is happy to help.
          </p>
          <a
            href="tel:+97156537850"
            className="btn-green"
            style={{ display: "inline-flex" }}
          >
            <i className="bi bi-telephone"></i> Speak to Our Team
          </a>
        </div>
      </div>
    </section>
  );
}

/* ─── WHY CHOOSE SECTION ─────────────────────────────────────────────────────── */
// function WhyChooseSection() {
//   const isMobile = useIsMobile();
//   const ref = useRef(null);
//   const vis = useInView(ref, 0.06);
//   const [animPct, setAnimPct] = useState(0);
//   useEffect(() => {
//     if (vis) {
//       let s = null;
//       const fn = (ts) => {
//         if (!s) s = ts;
//         const p = Math.min((ts - s) / 1600, 1);
//         setAnimPct(p);
//         if (p < 1) requestAnimationFrame(fn);
//       };
//       requestAnimationFrame(fn);
//     }
//   }, [vis]);

//   return (
//     <section
//       ref={ref}
//       style={{
//         background: "#0a0a0a",
//         padding: "min(120px,12vw) 24px",
//         position: "relative",
//         overflow: "hidden",
//       }}
//     >
//       <div
//         style={{
//           position: "absolute",
//           width: 700,
//           height: 700,
//           borderRadius: "50%",
//           background:
//             "radial-gradient(circle,rgba(45,1.3,30,0.1),transparent 70%)",
//           top: "-20%",
//           right: "-15%",
//           pointerEvents: "none",
//         }}
//       />

//       <div
//         style={{
//           maxWidth: 1312,
//           margin: "0 auto",
//           position: "relative",
//           zIndex: 1,
//         }}
//       >
//         <div style={{ display: "flex", gap: 80, alignItems: "center", flexWrap: "wrap" }}>
//           <div
//             style={{
//               flex: 1,
//               minWidth: 280,
//               opacity: vis ? 1 : 0,
//               transform: vis ? "translateY(0)" : "translateY(32px)",
//               transition: "all 0.8s ease",
//             }}
//           >
//             <span className="sl">The Fruits Garden Difference</span>
//             <h2
//               className="serif"
//               style={{
//                 fontSize: "clamp(36px,4.5vw,54px)",
//                 fontWeight: 800,
//                 letterSpacing: -2.5,
//                 color: "#fff",
//                 lineHeight: 1.05,
//                 marginBottom: 20,
//               }}
//             >
//               Why 200+ Businesses
//               <br />
//               Choose <span style={{ color: "#dd3c30" }}>Fruits Garden</span>
//             </h2>
//             <p
//               style={{
//                 fontSize: 16,
//                 color: "rgba(255,255,255,0.55)",
//                 lineHeight: 1.7,
//                 maxWidth: 420,
//                 marginBottom: 48,
//               }}
//             >
//               In 22 years, we've built our reputation on one simple promise —
//               the freshest produce, on time, every time. Here's what sets us
//               apart.
//             </p>
//             {[
//               ["On-Time Delivery Rate", 99],
//               ["Customer Retention", 94],
//               ["Quality Pass Rate", 98],
//               ["Cold Chain Accuracy", 99],
//             ].map(([l, p], i) => (
//               <div
//                 key={i}
//                 style={{
//                   marginBottom: 20,
//                   opacity: vis ? 1 : 0,
//                   transition: `all 0.6s ease ${0.2 + i * 0.1}s`,
//                   transform: vis ? "translateY(0)" : "translateY(16px)",
//                 }}
//               >
//                 <div
//                   style={{
//                     display: "flex",
//                     justifyContent: "space-between",
//                     fontSize: 13,
//                     color: "rgba(255,255,255,0.55)",
//                     marginBottom: 7,
//                   }}
//                 >
//                   <span>{l}</span>
//                   <strong style={{ color: "#dd3c30" }}>
//                     {Math.round(animPct * p)}%
//                   </strong>
//                 </div>
//                 <div
//                   style={{
//                     height: 6,
//                     background: "rgba(255,255,255,0.08)",
//                     borderRadius: 3,
//                     overflow: "hidden",
//                   }}
//                 >
//                   <div
//                     style={{
//                       height: "100%",
//                       background: "linear-gradient(90deg,#276530,#dd3c30)",
//                       width: `${animPct * p}%`,
//                       borderRadius: 3,
//                       transition: "none",
//                       boxShadow:
//                         animPct > 0.9
//                           ? "0 0 10px rgba(143,206,1.3,0.5)"
//                           : "none",
//                     }}
//                   />
//                 </div>
//               </div>
//             ))}
//           </div>

//           <div
//             style={{
//               flex: 1,
//               minWidth: 280,
//               display: "grid",
//               gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
//               gap: 12,
//             }}
//           >
//             {[
//               {
//                 icon: "bi-lightning",
//                 title: "Farm Fresh Daily",
//                 desc: "Products sourced and delivered within 24–48 hours of harvest for maximum freshness.",
//                 delay: 0,
//               },
//               {
//                 icon: "bi-snow",
//                 title: "Cold Chain Mastery",
//                 desc: "Temperature-controlled from 2–5°C at every stage of the supply journey.",
//                 delay: 0.1,
//               },
//               {
//                 icon: "bi-check-circle-fill",
//                 title: "Quality Plus",
//                 desc: "HACCP, ISO 9001 & ISO 22000 — the highest international food safety standards.",
//                 delay: 0.2,
//               },
//               {
//                 icon: "bi-globe-europe-africa",
//                 title: "15+ Source Countries",
//                 desc: "Year-round availability guaranteed through our global agricultural network.",
//                 delay: 0.3,
//               },
//               {
//                 icon: "bi-truck",
//                 title: "UAE-Wide Delivery",
//                 desc: "Every emirate, every day. Our dedicated fleet never misses a delivery.",
//                 delay: 0.4,
//               },
//               {
//                 icon: "bi-person",
//                 title: "Dedicated Account Manager",
//                 desc: "Every client gets a dedicated point of contact for personalized service.",
//                 delay: 0.5,
//               },
//             ].map((c, i) => (
//               <div
//                 key={i}
//                 className="card-hover"
//                 style={{
//                   background: "rgba(255,255,255,0.04)",
//                   border: "1px solid rgba(255,255,255,0.07)",
//                   borderRadius: 16,
//                   padding: "24px 20px",
//                   opacity: vis ? 1 : 0,
//                   transform: vis ? "translateY(0)" : "translateY(24px)",
//                   transition: `all 0.6s ease ${c.delay}s`,
//                 }}
//               >
//                 <div style={{ fontSize: 32, marginBottom: 20 }}>
//                   <i className={`bi ${c.icon}`} style={{ color: "#fff" }}></i>
//                 </div>
//                 <div
//                   style={{
//                     fontSize: 14,
//                     fontWeight: 700,
//                     color: "#fff",
//                     marginBottom: 8,
//                     lineHeight: 1.3,
//                   }}
//                 >
//                   {c.title}
//                 </div>
//                 <div
//                   style={{
//                     fontSize: 13,
//                     color: "rgba(255,255,255,0.45)",
//                     lineHeight: 1.55,
//                   }}
//                 >
//                   {c.desc}
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>

//         {/* Image strip */}
//         <div
//           style={{
//             display: "grid",
//             gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)",
//             gap: 12,
//             marginTop: 60,
//           }}
//         >
//           {[
//             "/assets/images/company/1.jpeg",
//             IMG.freshProduce,
//             "/assets/images/company/global.jpg",
//           ].map((img, i) => (
//             <div
//               key={i}
//               style={{
//                 borderRadius: 16,
//                 overflow: "hidden",
//                 height: 200,
//                 position: "relative",
//                 opacity: vis ? 1 : 0,
//                 transform: vis ? "translateY(0)" : "translateY(24px)",
//                 transition: `all 0.7s ease ${0.4 + i * 0.12}s`,
//               }}
//             >
//               <img
//                 src={img}
//                 alt=""
//                 style={{
//                   width: "100%",
//                   height: "100%",
//                   objectFit: "cover",
//                   filter: "brightness(1) saturate(1.1)",
//                 }}
//               />
//               <div
//                 style={{
//                   position: "absolute",
//                   inset: 0,
//                   background:
//                     "linear-gradient(to top,rgba(0,0,0,0.5),transparent 50%)",
//                 }}
//               />
//               <div
//                 style={{
//                   position: "absolute",
//                   bottom: 14,
//                   left: 16,
//                   fontSize: 12,
//                   fontWeight: 600,
//                   color: "rgba(255,255,255,0.7)",
//                   letterSpacing: 0.5,
//                 }}
//               >
//                 {["Our Warehouse", "Fresh Produce", "Global Network"][i]}
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     </section>
//   );
// }

/* ─── NEW: Live Chat & Response Time ─── */
function ResponsePromiseSection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref, 0.08);
  const channels = [
    {
      icon: "bi-telephone",
      label: "Phone",
      response: "< 2 min",
      desc: "Speak directly with a sales expert during business hours.",
      tag: "Fastest",
      tagColor: "#276530",
      hours: "Mon–Sat 8am–6pm",
    },
    {
      icon: "bi-chat-dots",
      label: "WhatsApp",
      response: "< 15 min",
      desc: "Send a message anytime and get a quick response from our team.",
      tag: "Popular",
      tagColor: "#25d366",
      hours: "Mon–Sat 7am–8pm",
    },
    {
      icon: "bi-envelope",
      label: "Email",
      response: "< 4 hrs",
      desc: "Detailed queries, bulk orders, and formal inquiries handled thoroughly.",
      tag: "Detailed",
      tagColor: "#3b82f6",
      hours: "24hr response",
    },
    {
      icon: "bi-clipboard-check",
      label: "Quote Form",
      response: "< 1 hr",
      desc: "Submit our structured quote form for the fastest pricing turnaround.",
      tag: "Recommended",
      tagColor: "#f59e0b",
      hours: "Business hours",
    },
  ];
  return (
    <section
      ref={ref}
      id="response"
      style={{ background: "#f7fdf4", padding: "min(80px,8vw) min(48px,4.8vw)" }}
    >
      <div style={{ maxWidth: 1312, margin: "0 auto" }}>
        <div
          style={{
            textAlign: "center",
            marginBottom: 60,
            opacity: vis ? 1 : 0,
            transform: vis ? "none" : "translateY(28px)",
            transition: "all 0.8s cubic-bezier(.22,1,.36,1)",
          }}
        >
          <div className="sl-dark">We're Here</div>
          <h2
            className="serif"
            style={{
              fontSize: "clamp(30px,4vw,50px)",
              fontWeight: 800,
              color: "#1a1a1a",
              letterSpacing: -2,
              lineHeight: 1.05,
            }}
          >
            Multiple Ways to <span style={{ color: "#276530" }}>Reach Us</span>
          </h2>
          <p
            style={{
              fontSize: 16,
              color: "#666",
              maxWidth: 500,
              margin: "16px auto 0",
              lineHeight: 1.3,
            }}
          >
            Choose your preferred channel. Every message is read by a real
            person.
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)",
            gap: 8,
          }}
        >
          {channels.map((c, i) => (
            <div
              key={i}
              style={{
                background: "#fff",
                borderRadius: 24,
                padding: "36px 28px",
                boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                border: "1px solid rgba(39,101,48,0.08)",
                position: "relative",
                overflow: "hidden",
                opacity: vis ? 1 : 0,
                transform: vis ? "none" : "translateY(40px) scale(0.96)",
                transition: `all 0.65s cubic-bezier(.22,1,.36,1) ${i * 0.1}s`,
                cursor: "default",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-6px)";
                e.currentTarget.style.boxShadow = "0 20px 48px rgba(0,0,0,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow = "0 4px 24px rgba(0,0,0,0.06)";
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  padding: "4px 10px",
                  borderRadius: 20,
                  background: `${c.tagColor}18`,
                  color: c.tagColor,
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: 0.5,
                }}
              >
                {c.tag}
              </div>
              <div style={{ fontSize: 40, marginBottom: 20 }}>
                <i className={`bi ${c.icon}`}></i>
              </div>
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#1a1a1a",
                  marginBottom: 8,
                }}
              >
                {c.label}
              </h3>
              <p
                style={{
                  fontSize: 13.5,
                  color: "#555",
                  lineHeight: 1.5,
                  marginBottom: 20,
                }}
              >
                {c.desc}
              </p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 900,
                      color: c.tagColor,
                      fontFamily: "Georgia,serif",
                    }}
                  >
                    {c.response}
                  </div>
                  <div style={{ fontSize: 11, color: "#aaa" }}>
                    avg. response
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#aaa",
                    textAlign: "right",
                    lineHeight: 1.4,
                  }}
                >
                  {c.hours}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── NEW: Interactive Location & Directions ─── */
function LocationSection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref, 0.08);
  const [activePoint, setActivePoint] = useState(null);
  const mapPoints = [
    {
      label: "Main Office",
      x: 48,
      y: 44,
      desc: "Dubai Food District, Block 3, Unit 98-99",
      type: "office",
    },
    {
      label: "Cold Storage",
      x: 42,
      y: 52,
      desc: "Temperature-controlled warehouse, 5,000 sqm",
      type: "warehouse",
    },
    {
      label: "Loading Bay",
      x: 55,
      y: 48,
      desc: "24/7 fleet dispatch for same-day deliveries",
      type: "dispatch",
    },
  ];
  return (
    <section
      ref={ref}
      id="location"
      style={{ background: "#fff", padding: "min(100px,10vw) min(48px,4.8vw)" }}
    >
      <div style={{ maxWidth: 1312, margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr",
            gap: 80,
            alignItems: "center",
          }}
        >
          {/* Info */}
          <div
            style={{
              opacity: vis ? 1 : 0,
              transform: vis ? "none" : "translateX(40px)",
              transition: "all 0.9s cubic-bezier(.22,1,.36,1) 0.15s",
            }}
          >
            <div className="sl-dark">Find Us</div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(28px,3.2vw,42px)",
                fontWeight: 800,
                color: "#1a1a1a",
                letterSpacing: -2,
                lineHeight: 1.1,
                marginBottom: 24,
              }}
            >
              Located at the Heart of Dubai's{" "}
              <span style={{ color: "#276530" }}>Food District</span>
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {[
                {
                  icon: "bi-geo-alt-fill",
                  label: "Address",
                  value: "Dubai Food District, Block 3, Unit 98-99, Dubai, UAE",
                  link: false,
                },
                {
                  icon: "bi-telephone",
                  label: "Sales",
                  value: "+971 56 537 8650",
                  link: "tel:+971565378650",
                },
                {
                  icon: "bi-telephone-fill",
                  label: "Office",
                  value: "+971 4 333 4629",
                  link: "tel:+97143334629",
                },
                {
                  icon: "bi-envelope",
                  label: "Email",
                  value: "sales@fruitsgarden.com",
                  link: "mailto:sales@fruitsgarden.com",
                },
                {
                  icon: "bi-clock-history",
                  label: "Hours",
                  value: "Mon–Sat: 8:00 AM – 6:00 PM",
                  link: false,
                },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 16 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: "#f7fdf4",
                      border: "1.5px solid rgba(39,101,48,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    <i className={`bi ${item.icon}`}></i>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        letterSpacing: 1,
                        color: "#276530",
                        fontWeight: 700,
                        marginBottom: 2,
                        textTransform: "uppercase",
                      }}
                    >
                      {item.label}
                    </div>
                    {item.link ? (
                      <a
                        href={item.link}
                        style={{
                          fontSize: 15,
                          color: "#1a1a1a",
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                        onMouseEnter={(e) => (e.target.style.color = "#276530")}
                        onMouseLeave={(e) => (e.target.style.color = "#1a1a1a")}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <div
                        style={{
                          fontSize: 15,
                          color: "#1a1a1a",
                          fontWeight: 500,
                        }}
                      >
                        {item.value}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <a
              href="https://maps.app.goo.gl/iyqCCCWyLk7nJ4pQ6"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-green"
              style={{ display: "inline-flex", marginTop: 32, gap: 8 }}
            >
              <i className="bi bi-geo-alt-fill"></i> Open in Google Maps
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function BackTop({ scrollY }) {
  if (scrollY < 600) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      style={{
        position: "fixed",
        bottom: 32,
        right: 32,
        zIndex: 999,
        width: 48,
        height: 48,
        borderRadius: "50%",
        background: "#dd3c30",
        color: "#fff",
        border: "none",
        cursor: "pointer",
        fontSize: 18,
        boxShadow: "0 4px 18px rgba(45,1.3,30,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "transform 0.2s,box-shadow 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.12)";
        e.currentTarget.style.boxShadow = "0 8px 30px rgba(45,1.3,30,0.6)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.boxShadow = "0 4px 18px rgba(45,1.3,30,0.45)";
      }}
    >
      ↑
    </button>
  );
}

/* ─── ROOT ───────────────────────────────────────────────────────────────────── */
export default function ContactUs() {
  const scrollY = useScrollY();
  useEffect(() => {
    document.title = "Contact Us — Fruits Garden Trading LLC";
    const style = document.createElement("style");
    style.textContent = GLOBAL_CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  return (
    <div>
      <Navbar scrollY={scrollY} />
      <HeroSection scrollY={scrollY} />
      <ResponsePromiseSection />
      <FormSection />
      <LocationSection />
      <OfficeSection />
      {/* <WhyChooseSection /> */}
      <FAQSection />
      <Footer />
      <BackTop scrollY={scrollY} />
    </div>
  );
}
